import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';
import { Settings, CreditCard, Save, Building2, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function TenantConfiguration({ user }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    establishment_name: '',
    establishment_code: '',
    orange_money_number: '',
    orange_money_name: '',
    payment_instructions: '',
    currency: 'GNF',
    enable_online_payment: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/tenant/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les paramètres',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validation du numéro Orange Money
    if (settings.orange_money_number && !settings.orange_money_number.startsWith('+224')) {
      toast({
        title: 'Erreur',
        description: 'Le numéro Orange Money doit commencer par +224',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/api/tenant/settings`, settings);
      
      toast({
        title: 'Succès',
        description: 'Paramètres enregistrés avec succès',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la sauvegarde',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Settings className="w-8 h-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold">Configuration de l'Établissement</h1>
          <p className="text-gray-600 mt-1">
            Gérez les paramètres de paiement et les informations de votre établissement
          </p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Informations de l'établissement */}
        <Card className="card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-blue-600" />
              Informations de l'Établissement
            </CardTitle>
            <CardDescription>
              Ces informations sont en lecture seule et gérées par le super administrateur
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nom de l'Établissement</Label>
              <Input
                value={settings.establishment_name}
                disabled
                className="bg-gray-100"
              />
            </div>
            
            <div>
              <Label>Code Établissement</Label>
              <Input
                value={settings.establishment_code}
                disabled
                className="bg-gray-100"
              />
            </div>
          </CardContent>
        </Card>

        {/* Configuration Orange Money */}
        <Card className="card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="w-5 h-5 mr-2 text-orange-600" />
              Configuration Orange Money
            </CardTitle>
            <CardDescription>
              Configurez votre compte Orange Money pour recevoir les paiements des frais de scolarité
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="orange_money_number">
                Numéro Orange Money <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-500" />
                <Input
                  id="orange_money_number"
                  type="tel"
                  placeholder="+224 6XX XX XX XX"
                  value={settings.orange_money_number}
                  onChange={(e) => handleChange('orange_money_number', e.target.value)}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Format: +224 6XX XX XX XX (ex: +224 620 12 34 56)
              </p>
            </div>

            <div>
              <Label htmlFor="orange_money_name">
                Nom du Titulaire du Compte
              </Label>
              <Input
                id="orange_money_name"
                type="text"
                placeholder="Nom complet du titulaire"
                value={settings.orange_money_name}
                onChange={(e) => handleChange('orange_money_name', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="payment_instructions">
                Instructions de Paiement (optionnel)
              </Label>
              <Textarea
                id="payment_instructions"
                placeholder="Instructions additionnelles pour les parents lors du paiement..."
                value={settings.payment_instructions}
                onChange={(e) => handleChange('payment_instructions', e.target.value)}
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">
                Ces instructions seront affichées aux parents lors du paiement
              </p>
            </div>

            <div className="flex items-center space-x-2 p-4 bg-blue-50 rounded-lg">
              <Switch
                id="enable_online_payment"
                checked={settings.enable_online_payment}
                onCheckedChange={(checked) => handleChange('enable_online_payment', checked)}
              />
              <Label htmlFor="enable_online_payment" className="cursor-pointer">
                Activer les paiements en ligne Orange Money
              </Label>
            </div>

            {!settings.enable_online_payment && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Les paiements en ligne sont désactivés. Les parents ne pourront pas payer via Orange Money.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bouton Enregistrer */}
        <div className="flex justify-end">
          <Button 
            type="submit" 
            disabled={saving}
            className="btn-primary"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer les Paramètres'}
          </Button>
        </div>
      </form>

      {/* Informations de sécurité */}
      <Card className="card border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-blue-900 mb-2">🔒 Sécurité et Confidentialité</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Votre numéro Orange Money est stocké de manière sécurisée</li>
            <li>• Seuls les administrateurs de votre établissement peuvent voir ces informations</li>
            <li>• Les paiements sont traités directement par Orange Money Guinée</li>
            <li>• Vérifiez régulièrement votre solde Orange Money pour les paiements reçus</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export default TenantConfiguration;
